import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, CalendarDays, MapPin, Mail, Phone, XCircle, CalendarSync, Receipt, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h <= 20; h++) {
    for (const m of ['00', '30']) {
      if (h === 20 && m === '30') continue;
      const value = `${String(h).padStart(2, '0')}:${m}`;
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      slots.push({ value, label: `${hour12}:${m} ${h < 12 ? 'AM' : 'PM'}` });
    }
  }
  return slots;
})();

function formatTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const openReschedule = (meetingId) => {
    setRescheduleId(rescheduleId === meetingId ? null : meetingId);
    setNewDate('');
    setNewTime('');
    setNote('');
    setFormError('');
  };

  const submitReschedule = async (meetingId) => {
    if (!newDate) {
      setFormError('Please pick a new date.');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/portal/${token}/reschedule`, {
        meeting_id: meetingId,
        new_date: newDate,
        new_time: newTime || undefined,
        note: note || undefined,
      });
      setData((prev) => ({
        ...prev,
        upcoming: prev.upcoming.map((m) =>
          m.id === meetingId
            ? { ...m, reschedule_pending: true, requested_date: newDate, requested_time: newTime }
            : m
        ),
      }));
      setRescheduleId(null);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  useEffect(() => {
    axios
      .get(`${API}/public/portal/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <XCircle className="w-10 h-10 text-alert mb-4" />
        <h1 className="text-2xl font-serif font-bold text-foreground mb-2">Link Not Found</h1>
        <p className="text-text-secondary">This portal link is invalid.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-md mx-auto space-y-6 py-8" data-testid="client-portal">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Camera className="w-8 h-8 text-background" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-foreground">{data.business_name}</h1>
          <p className="text-text-secondary mt-1" data-testid="portal-greeting">
            Hi {data.client_name.split(' ')[0]} — here are your sessions
          </p>
        </div>

        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-primary uppercase tracking-wider">
            Upcoming Sessions ({data.upcoming.length})
          </h2>
          {data.upcoming.length === 0 ? (
            <div className="bg-surface border border-border rounded-xl p-6 text-center" data-testid="portal-empty">
              <p className="text-text-secondary text-sm">No upcoming sessions scheduled.</p>
            </div>
          ) : (
            data.upcoming.map((m, i) => (
              <div key={m.id || i} className="bg-surface border border-border rounded-xl p-4" data-testid={`portal-session-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-foreground">{m.title}</div>
                    <div className="text-sm text-text-secondary mt-0.5">
                      {m.date}
                      {m.time && ` · ${formatTime(m.time)}`}
                    </div>
                    {m.location && (
                      <div className="text-xs text-text-muted mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {m.location}
                      </div>
                    )}

                    {m.reschedule_pending ? (
                      <div
                        className="mt-3 text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5"
                        data-testid={`reschedule-pending-${i}`}
                      >
                        <CalendarSync className="w-3.5 h-3.5 flex-shrink-0" />
                        Date change requested → {m.requested_date}
                        {m.requested_time && ` at ${formatTime(m.requested_time)}`} (awaiting approval)
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => openReschedule(m.id)}
                          className="mt-3 text-xs text-primary hover:underline flex items-center gap-1"
                          data-testid={`request-reschedule-${i}`}
                        >
                          <CalendarSync className="w-3.5 h-3.5" />
                          Request a date change
                        </button>

                        {rescheduleId === m.id && (
                          <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid={`reschedule-form-${i}`}>
                            <div className="flex gap-2">
                              <input
                                type="date"
                                value={newDate}
                                min={new Date().toISOString().split('T')[0]}
                                onChange={(e) => setNewDate(e.target.value)}
                                className="flex-1 bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:border-primary outline-none"
                                data-testid="reschedule-date-input"
                              />
                              <select
                                value={newTime}
                                onChange={(e) => setNewTime(e.target.value)}
                                className="w-32 bg-surface-raised border border-border rounded-lg px-2 py-2 text-sm text-foreground focus:border-primary outline-none"
                                data-testid="reschedule-time-select"
                              >
                                <option value="">Any time</option>
                                {TIME_SLOTS.map((slot) => (
                                  <option key={slot.value} value={slot.value}>
                                    {slot.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <input
                              type="text"
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="Reason (optional)"
                              className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-text-muted focus:border-primary outline-none"
                              data-testid="reschedule-note-input"
                            />
                            {formError && (
                              <p className="text-xs text-alert" data-testid="reschedule-error">{formError}</p>
                            )}
                            <button
                              onClick={() => submitReschedule(m.id)}
                              disabled={submitting}
                              className="bg-primary text-background hover:bg-primary/90 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
                              data-testid="submit-reschedule-btn"
                            >
                              {submitting ? 'Sending...' : 'Send Request'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          {data.past_count > 0 && (
            <p className="text-xs text-text-muted text-center">
              {data.past_count} past session{data.past_count === 1 ? '' : 's'} together
            </p>
          )}
        </div>

        <div className="space-y-3" data-testid="portal-invoices">
          <h2 className="text-sm font-semibold text-primary uppercase tracking-wider">
            Invoices
          </h2>
          {data.open_invoices.length === 0 ? (
            <div className="bg-surface border border-border rounded-xl p-4 flex items-center gap-3" data-testid="portal-invoices-empty">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0" />
              <p className="text-sm text-text-secondary">Nothing due — you're all paid up!</p>
            </div>
          ) : (
            <>
              {data.open_invoices.map((inv, i) => {
                const today = new Date().toISOString().split('T')[0];
                const overdue = inv.due_date && inv.due_date < today;
                return (
                  <div key={inv.id || `${inv.description}-${inv.date}-${i}`} className="bg-surface border border-border rounded-xl p-4" data-testid={`portal-invoice-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                          <Receipt className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate">{inv.description}</div>
                          <div className="text-xs text-text-muted mt-0.5">
                            {inv.due_date ? (
                              overdue ? (
                                <span className="text-alert font-medium">Overdue — was due {inv.due_date}</span>
                              ) : (
                                `Due ${inv.due_date}`
                              )
                            ) : (
                              `Issued ${inv.date}`
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="font-semibold text-foreground flex-shrink-0">
                        ${(inv.amount || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 flex items-center justify-between" data-testid="portal-total-due">
                <span className="text-sm font-medium text-foreground">Total due</span>
                <span className="text-lg font-bold text-primary">${data.total_due.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>

        {(data.contact_email || data.contact_phone) && (
          <div className="bg-surface border border-border rounded-xl p-4 space-y-2" data-testid="portal-contact">
            <h3 className="text-sm font-medium text-foreground">
              Questions? Reach {data.photographer_name || 'us'}
            </h3>
            {data.contact_email && (
              <a href={`mailto:${data.contact_email}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                <Mail className="w-4 h-4" />
                {data.contact_email}
              </a>
            )}
            {data.contact_phone && (
              <a href={`tel:${data.contact_phone}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                <Phone className="w-4 h-4" />
                {data.contact_phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
